import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ReplayOutlinedIcon from "@mui/icons-material/ReplayOutlined";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import { Box, CircularProgress, Link, Paper, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import type React from "react";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Link as RouterLink, useParams } from "react-router-dom";
import {
  approveSuratJalanApi,
  getSuratJalanByIdApi,
  verifySuratJalanApi,
} from "../api/suratJalanApi";
import DetailHeaderSplitActions, {
  type DetailHeaderAction,
} from "../components/common/DetailHeaderSplitActions";
import DetailPageHeader from "../components/common/DetailPageHeader";
import StatusWorkflowStepper from "../components/common/StatusWorkflowStepper";
import OrderContextSummaryCards from "../components/orders/OrderContextSummaryCards";
import OrderHistorySection from "../components/orders/OrderHistorySection";
import SuratJalanRequestRevisionDialog from "../components/suratJalan/SuratJalanRequestRevisionDialog";
import { TestId } from "../constants/testIds";
import type { RootState } from "../store";
import type translations from "../translations/translations.json";
import type { SuratJalanDetail } from "../types/suratJalan";
import { SuratJalanStatus } from "../types/suratJalan";
import { getSuratJalanActionAvailability } from "../utils/suratJalanActions";
import { getErrorMessage } from "../utils/errorHandling";
import { SURAT_JALAN_WORKFLOW_STEP_KEYS } from "../utils/suratJalanStatusProgress";
import { toastEmitter } from "../utils/toastEmitter";

type TranslationKeys = keyof typeof translations.en;

const SuratJalanDetailPage: React.FC = () => {
  const { id } = useParams();
  const { t } = useTranslation();
  const user = useSelector((state: RootState) => state.user.user);
  const [suratJalan, setSuratJalan] = useState<SuratJalanDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [revisionOpen, setRevisionOpen] = useState(false);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      setSuratJalan(await getSuratJalanByIdApi(id));
    } catch (err) {
      setError(getErrorMessage(err, t, "getSuratJalan"));
    } finally {
      setLoading(false);
    }
  }, [id, t]);

  useEffect(() => {
    void load();
  }, [load]);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !suratJalan) {
    return (
      <Paper sx={{ p: 3 }}>
        <Typography color="error">
          {error || t("suratJalan.notFound" as TranslationKeys)}
        </Typography>
      </Paper>
    );
  }

  const { canVerify, canApprove, canRevise } = getSuratJalanActionAvailability(
    user,
    suratJalan.status,
  );

  const run = async (action: () => Promise<SuratJalanDetail>, successKey: string) => {
    setActionLoading(true);
    try {
      setSuratJalan(await action());
      toastEmitter.emit(t(successKey as TranslationKeys), "success");
    } catch (err) {
      toastEmitter.emit(getErrorMessage(err, t, "suratJalanAction"), "error");
    } finally {
      setActionLoading(false);
    }
  };

  const verifyAction: DetailHeaderAction = {
    id: "verify",
    label: t("suratJalan.verify" as TranslationKeys),
    icon: <CheckCircleOutlineIcon fontSize="small" />,
    hidden: !canVerify,
    disabled: actionLoading,
    onClick: () => void run(() => verifySuratJalanApi(suratJalan._id), "suratJalan.verified"),
    testId: TestId.SURAT_JALAN.VERIFY,
  };
  const approveAction: DetailHeaderAction = {
    id: "approve",
    label: t("suratJalan.approve" as TranslationKeys),
    icon: <TaskAltIcon fontSize="small" />,
    hidden: !canApprove,
    disabled: actionLoading,
    onClick: () => void run(() => approveSuratJalanApi(suratJalan._id), "suratJalan.approved"),
    testId: TestId.SURAT_JALAN.APPROVE,
  };
  const reviseAction: DetailHeaderAction = {
    id: "revise",
    label: t("orders.requestRevision" as TranslationKeys),
    icon: <ReplayOutlinedIcon fontSize="small" />,
    hidden: !canRevise,
    disabled: actionLoading,
    onClick: () => setRevisionOpen(true),
    testId: TestId.SURAT_JALAN.REQUEST_REVISION,
  };

  const visible = [verifyAction, approveAction, reviseAction].filter((action) => !action.hidden);
  const primary = visible[0] ?? {
    ...verifyAction,
    hidden: false,
    disabled: true,
  };
  const menuActions = visible.filter((action) => action.id !== primary.id);
  const steps = SURAT_JALAN_WORKFLOW_STEP_KEYS.map((key) => ({
    key,
    label: t(key as TranslationKeys),
  }));

  return (
    <Box data-testid={TestId.SURAT_JALAN.DETAIL} sx={{ pb: 4 }}>
      <DetailPageHeader
        compact
        title={t("Surat Jalan" as TranslationKeys)}
        subtitle={
          <Box component="span" data-testid={TestId.SURAT_JALAN.READABLE_ID}>
            {suratJalan.readableId}
          </Box>
        }
        statusLabel={t("Status" as TranslationKeys)}
        status={suratJalan.status}
        statusTestId={TestId.SURAT_JALAN.STATUS}
        progressSlot={
          <StatusWorkflowStepper compact steps={steps} activeStepKey={suratJalan.status} />
        }
        statusNote={
          suratJalan.status === SuratJalanStatus.REVISION_REQUIRED && suratJalan.revisionNote ? (
            <Paper
              variant="outlined"
              sx={{
                px: 1.5,
                py: 0.75,
                bgcolor: (theme) => alpha(theme.palette.warning.main, 0.1),
                borderColor: "warning.main",
              }}
            >
              <Typography variant="caption" color="warning.dark" sx={{ fontWeight: 700 }}>
                {t("orders.revisionNote" as TranslationKeys)}
              </Typography>
              <Typography variant="body2">{suratJalan.revisionNote.note}</Typography>
            </Paper>
          ) : undefined
        }
        actions={
          visible.length > 0 ? (
            <DetailHeaderSplitActions
              primary={primary}
              menuActions={menuActions}
              menuTestId={TestId.SURAT_JALAN.ACTIONS_MENU}
            />
          ) : undefined
        }
      />

      <Box sx={{ display: "flex", gap: 3, mb: 2, flexWrap: "wrap" }}>
        {suratJalan.order && (
          <Link
            component={RouterLink}
            to={`/orders/${suratJalan.order.readableId}`}
            data-testid={TestId.SURAT_JALAN.ORDER_LINK}
          >
            {t("Orders" as TranslationKeys)}: {suratJalan.order.readableId}
          </Link>
        )}
        {suratJalan.uangJalanReport && (
          <Link
            component={RouterLink}
            to={`/uang-jalan-reports/${suratJalan.uangJalanReport.readableId}`}
            data-testid={TestId.SURAT_JALAN.REPORT_LINK}
          >
            {t("Uang Jalan Report" as TranslationKeys)}: {suratJalan.uangJalanReport.readableId}
          </Link>
        )}
      </Box>

      {suratJalan.order && <OrderContextSummaryCards order={suratJalan.order} hideSuratJalanLink />}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
          gap: 2,
          alignItems: "start",
        }}
      >
        <Box>
          <OrderHistorySection
            histories={suratJalan.histories}
            revisionNote={suratJalan.revisionNote}
            title={t("suratJalan.histories" as TranslationKeys)}
            testIds={{
              root: TestId.SURAT_JALAN.HISTORY,
              entry: TestId.SURAT_JALAN.HISTORY_ENTRY,
              summary: TestId.SURAT_JALAN.HISTORY_SUMMARY,
              change: TestId.SURAT_JALAN.HISTORY_CHANGE,
            }}
            sx={{ mt: 2 }}
          />
        </Box>
        <Box>
          {suratJalan.order && (
            <OrderHistorySection
              histories={suratJalan.order.histories ?? []}
              revisionNote={suratJalan.order.revisionNote}
              title={t("orders.orderChangeHistory" as TranslationKeys)}
              sx={{ mt: 2 }}
            />
          )}
        </Box>
      </Box>

      <SuratJalanRequestRevisionDialog
        suratJalanId={suratJalan._id}
        open={revisionOpen}
        onClose={() => setRevisionOpen(false)}
        onSuccess={setSuratJalan}
      />
    </Box>
  );
};

export default SuratJalanDetailPage;
